/**
 * Round scene — main gameplay scene for Anagram (continuous-play variant).
 *
 * Layout (1920×1080):
 *   - Centered "card title" category label above the tiles (Oswald, ~70px).
 *   - Letter tiles in the middle, max-width 1180px so they never overflow into
 *     the right-side leaderboard panel.
 *   - Footer: "Type the word in chat" CTA. Correct guesses no longer render a
 *     green +points badge — every guess (correct or wrong) just spawns a
 *     rising chat bubble through `guess-bubbles.ts`, matching the
 *     Survey Says / WhatIs visual language. The session leaderboard on the
 *     right is the canonical view for who scored what.
 *
 * Hints reveal progressively across the round timer: each `an_hint_reveal`
 * snaps one letter card to its CORRECT position via FLIP, with a confetti
 * burst and a hint SFX. Late guessers can still puzzle the rest of the
 * jumble for diminishing payouts.
 *
 * Top-left countdown is owned by main.ts.
 */

import type { RoundState } from '../state/types.js';
import { createLetterTiles, type LetterTilesHandle } from '../ui/letter-tiles.js';
import { createCountdown, type CountdownHandle } from '../ui/countdown.js';
import { createSparkles, type SparklesHandle } from '../ui/sparkles.js';
import { createGuessBubbles, type GuessBubblesHandle } from '../ui/guess-bubbles.js';
import { sfx } from '../audio/sfx.js';

export interface RoundHandle {
  el: HTMLDivElement;
  setRound(r: RoundState): void;
  setVisible(v: boolean): void;
  /** Hook for main.ts to drive the end-of-round cascade reveal. */
  cascadeReveal(): Promise<void>;
  /** Spawn a rising guess bubble (called from main.ts on an_guess_received). */
  spawnGuessBubble(guess: string): void;
  tick(): void;
}

const STYLES = `
  @keyframes an-cta-pulse {
    0%, 100% { opacity: 0.85; }
    50%      { opacity: 1.0; }
  }
  .an-round-wrap {
    position: absolute;
    inset: 0;
    z-index: 30;
    display: grid;
    grid-template-rows: auto 1fr auto;
  }
  .an-round-category {
    /* Centered horizontally over the tile area (clear of the LB at 1500..1880). */
    padding: 80px 460px 0 280px;
    text-align: center;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 76px;
    font-weight: 700;
    letter-spacing: 0.18em;
    line-height: 0.95;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 6px 0 var(--sg-secondary, #060CE9), 0 0 40px rgba(255,196,61,0.35);
    text-transform: uppercase;
    pointer-events: none;
  }
  .an-round-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* Reserve room for side-leaderboard at right=1500..1880 (380w + gutter). */
    padding: 12px 460px 20px 80px;
    min-height: 0;
  }
  .an-round-foot {
    padding: 12px 460px 24px 80px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    font-family: 'Inter', system-ui, sans-serif;
    color: #E2E8F0;
    font-size: 22px;
    letter-spacing: 0.06em;
    min-height: 56px;
  }
  .an-round-cta {
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    font-size: 32px;
    letter-spacing: 0.30em;
    color: var(--sg-primary, #FFC43D);
    text-shadow: 0 0 20px rgba(255,196,61,0.45), 0 4px 6px rgba(0,0,0,0.6);
    text-transform: uppercase;
    animation: an-cta-pulse 2.6s ease-in-out infinite;
    pointer-events: none;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('an-round-style')) return;
  const s = document.createElement('style');
  s.id = 'an-round-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

export function createRound(stage: HTMLElement): RoundHandle {
  ensureStyles();
  const el = document.createElement('div');
  el.className = 'an-round-wrap';
  el.setAttribute('data-scene', 'round');
  el.setAttribute('data-testid', 'an-round');

  const category = document.createElement('div');
  category.className = 'an-round-category';
  category.setAttribute('data-testid', 'an-round-category');

  const body = document.createElement('div');
  body.className = 'an-round-body';

  const foot = document.createElement('div');
  foot.className = 'an-round-foot';
  const cta = document.createElement('span');
  cta.className = 'an-round-cta';
  cta.textContent = 'Type the word in chat';
  foot.appendChild(cta);

  el.appendChild(category);
  el.appendChild(body);
  el.appendChild(foot);
  stage.appendChild(el);

  const tiles: LetterTilesHandle = createLetterTiles(body);
  const countdown: CountdownHandle = createCountdown(el);
  const sparkles: SparklesHandle = createSparkles(el);
  // Bubbles overlay sits inside the round wrap at the bottom of the
  // 1180px content area so it doesn't fight the side leaderboard.
  const bubbles: GuessBubblesHandle = createGuessBubbles(el);

  let cur: RoundState | null = null;
  let lastWordId = '';
  /** How many hint reveals we've already snapped for the current word. */
  let snappedHintCount = 0;
  /**
   * Hint reveals can arrive faster than the FLIP animation completes; we
   * queue them and pop one per ~520ms so each gets its full snap + flash
   * before the next runs.
   */
  let hintQueue: Array<{ position: number; letter: string }> = [];
  let hintProcessing = false;
  const HINT_PROCESS_INTERVAL_MS = 540;

  /**
   * Drain the hint queue one entry at a time, spaced by
   * HINT_PROCESS_INTERVAL_MS so the FLIP animation from each snap settles
   * before the next runs. Re-arms via setTimeout while items remain.
   */
  function processHintQueueOnce(): void {
    if (hintQueue.length === 0) {
      hintProcessing = false;
      return;
    }
    const next = hintQueue.shift()!;
    tiles.snapHintLetter(next.letter);
    sfx.hint();
    const cx = 1920 / 2;
    const cy = 1080 / 2 - 40;
    sparkles.burst({ x: cx, y: cy, count: 36, speed: 1.0 });
    setTimeout(processHintQueueOnce, HINT_PROCESS_INTERVAL_MS);
  }

  function paint(): void {
    if (!cur) return;
    category.textContent = cur.category ? cur.category.toUpperCase() : '';

    if (cur.wordId !== lastWordId) {
      lastWordId = cur.wordId;
      snappedHintCount = 0;
      hintQueue = [];
      hintProcessing = false;
      tiles.setWord(cur.jumbled, cur.answer);
      // Stagger keyboard-clack to mirror the tile drop-in stagger.
      const visible = cur.jumbled.replace(/\s/g, '');
      for (let i = 0; i < visible.length; i++) {
        setTimeout(() => sfx.clack(), i * 60);
      }
    }

    // Progressive hint reveals — append every freshly-arrived event to the
    // queue so each gets its own snap + flash, even when multiple hints
    // arrive back-to-back (e.g. for repeated letters in BANANA / BUTTERFLY).
    while (snappedHintCount < cur.hintReveals.length) {
      hintQueue.push(cur.hintReveals[snappedHintCount]!);
      snappedHintCount += 1;
    }
    if (!hintProcessing && hintQueue.length > 0) {
      hintProcessing = true;
      processHintQueueOnce();
    }

    // Drive the countdown widget. Endpoint is wall-clock from backend.
    // countdownStartsAtMs (when set + future) holds the visible timer at the
    // full duration until the broadcast-relay grace passes.
    countdown.setPhase('GUESS', cur.endsAtMs, cur.countdownStartsAtMs ?? undefined);
  }

  return {
    el,
    setRound(r) {
      cur = r;
      paint();
    },
    setVisible(v) {
      el.style.display = v ? 'grid' : 'none';
      if (!v) {
        countdown.hide();
        bubbles.clear();
      }
    },
    cascadeReveal() {
      return tiles.cascadeReveal();
    },
    spawnGuessBubble(guess) {
      bubbles.spawn(guess);
    },
    tick() {
      paint();
    },
  };
}
